import { useEffect, useState } from 'react'
import { Flex } from '@radix-ui/themes'
import { useGetUsers } from '@/services/userService'
import { PagingInfo } from '@/types/user'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'
import SearchBox from '@/components/ui/SearchBox'
import BaseContainer from '@/components/ui/base-container'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import PageTitle from '@/components/PageTitle'
import { Main } from '@/components/layout/main'
import UsersTable from './components/users-table'
import { UserFilterTypes, UserRoles, UserStatus, UserTabs } from './constants'

const Users = () => {
  const [tab, setTab] = useState('all')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('all')
  const [role, setRole] = useState('all')
  const [type, setType] = useState('all')

  const debouncedQuery = useDebounce(query)

  const { users, isLoadingUsers, meta } = useGetUsers({
    role,
    page,
    limit: 10,
    search: debouncedQuery,
    is_active: status,
    unassigned: tab,
    type,
  })

  useEffect(() => {
    setPage(1)
  }, [debouncedQuery])

  const getStatusLabel = () => {
    return UserStatus.find((item) => item.value === status)?.label
  }

  const getRoleLabel = () => {
    return UserRoles.find((item) => item.value === role)?.label
  }

  const getTypeLabel = () => {
    return UserFilterTypes.find((item) => item.value === type)?.label
  }

  return (
    <Main>
      <PageTitle title='User Management' />
      <div>
        <BaseContainer
          action={
            <Flex
              align={'center'}
              justify={'between'}
              className='w-full gap-2.5'
            >
              <Flex className='rounded-[10px] bg-[#F6F7F9] p-1' gap={'1'}>
                {UserTabs.map((item) => (
                  <div
                    className={cn(
                      'cursor-pointer rounded-[6px] px-3 py-1.5 text-sm capitalize',
                      tab === item.value
                        ? 'bg-white text-[#002652] capitalize shadow-[0_1px_2px_#023A7C1A]'
                        : 'bg-transparent text-[#667085]'
                    )}
                    onClick={() => {
                      setTab(item.value)
                      setPage(1)
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </Flex>
              <Flex align={'center'} gap={'15px'}>
                <SearchBox
                  containerClass='h-10 min-w-[395px] max-w-[395px]'
                  onValueChange={(value: string) => setQuery(value)}
                />

                {/* type  */}
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className='h-10 min-h-10 w-fit md:w-[150px]'>
                    <SelectValue className='text-sm'>
                      <span className='text-[#101828]'>Type:</span>
                      <span className='font-light text-[#475467]'>
                        {getTypeLabel()}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {UserFilterTypes.map((item) => (
                      <SelectItem
                        onClick={() => {
                          setType(item.value)
                          setPage(1)
                        }}
                        value={item.value}
                      >
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* status  */}
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className='h-10 min-h-10 w-fit md:w-[150px]'>
                    <SelectValue className='text-sm'>
                      <span className='text-[#101828]'>Status:</span>
                      <span className='font-light text-[#475467]'>
                        {getStatusLabel()}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {UserStatus.map((item) => (
                      <SelectItem
                        onClick={() => {
                          setStatus(item.value)
                          setPage(1)
                        }}
                        value={item.value}
                      >
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* role  */}
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className='h-10 min-h-10 w-fit md:w-[150px]'>
                    <SelectValue className='text-sm'>
                      <span className='text-[#101828]'>Role:</span>
                      <span className='font-light text-[#475467]'>
                        {getRoleLabel()}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {UserRoles.map((item) => (
                      <SelectItem
                        onClick={() => {
                          setRole(item.value)
                          setPage(1)
                        }}
                        value={item.value}
                      >
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Flex>
            </Flex>
          }
        >
          <UsersTable
            isLoading={isLoadingUsers}
            meta={meta as PagingInfo}
            usersData={users}
            page={page}
            handlePageChange={setPage}
          />
        </BaseContainer>
      </div>
    </Main>
  )
}

export default Users
